import React, { createContext, useContext, useEffect, useState } from 'react'
import { User } from '@supabase/supabase-js'
import { supabase } from '../utils/supabase'
import { setIsDemo } from '../utils'
interface AuthContextType {
  user: User | null
  loading: boolean
  isFullGameActivated: boolean
  checkGameActivation: () => Promise<void>
  signIn: (email: string) => Promise<void>
  signInWithProvider: (provider: 'google' | 'discord') => Promise<void>
  signOut: () => Promise<void>
  ensureAnonymousAuth: () => Promise<User | null>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [isFullGameActivated, setIsFullGameActivated] = useState(false)

  useEffect(() => {
    // Check active session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null)
      setLoading(false)
    })

    // Listen for auth changes
    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null)
    })

    return () => subscription.unsubscribe()
  }, [])

  const ensureAnonymousAuth = async () => {
    const session = await supabase.auth.getSession()
    if (session.data.session?.user) return session.data.session.user

    const {
      data: { user: anonymousUser },
      error
    } = await supabase.auth.signInAnonymously()
    if (error) throw error
    return anonymousUser
  }

  const signIn = async (email: string) => {
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: window.location.origin
      }
    })
    if (error) throw error
  }

  const signOut = async () => {
    const { error } = await supabase.auth.signOut()
    if (error) throw error
  }

  const signInWithProvider = async (provider: 'google' | 'discord') => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        queryParams: {
          // Link to anonymous account if exists
          anonymous: user?.id || ''
        },
        redirectTo: window.location.origin
      }
    })
    if (error) throw error
  }

  const checkGameActivation = async () => {
    try {
      const { data, error } = await supabase
        .from('game_activation')
        .select('activation_key, expires_at')
        .eq('user_id', user?.id)
      // If no rows found, data will be empty array instead of null
      if (error) throw error

      const activation = data?.[0]
      if (activation) {
        // Check if activation hasn't expired
        const isValid = !activation.expires_at || new Date(activation.expires_at) > new Date()
        setIsFullGameActivated(isValid)
      } else {
        setIsFullGameActivated(false)
      }
    } catch (error) {
      //console.error('Error checking game activation:', error)
      setIsFullGameActivated(false)
    }
  }

  useEffect(() => {
    if (user) {
      checkGameActivation().then(() => {
        setIsDemo(!isFullGameActivated)
      })
    } else {
      setIsDemo(true)
    }
  }, [user, isFullGameActivated])

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isFullGameActivated,
        checkGameActivation,
        signIn,
        signInWithProvider,
        signOut,
        ensureAnonymousAuth
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
